import { useState } from "react";
import type { Opportunity, MonitorPlatform } from "../../../../shared/types";

const PLATFORM_LABELS: Record<MonitorPlatform, string> = {
  reddit: "Reddit",
  twitter: "Twitter/X",
  hackernews: "Hacker News",
  linkedin: "LinkedIn",
  quora: "Quora",
  indiehackers: "Indie Hackers",
  producthunt: "Product Hunt",
};

const PLATFORM_COLORS: Record<MonitorPlatform, string> = {
  reddit: "bg-orange-100 text-orange-700",
  twitter: "bg-sky-100 text-sky-700",
  hackernews: "bg-amber-100 text-amber-700",
  linkedin: "bg-blue-100 text-blue-700",
  quora: "bg-red-100 text-red-700",
  indiehackers: "bg-indigo-100 text-indigo-700",
  producthunt: "bg-rose-100 text-rose-700",
};

function scoreColor(score: number): string {
  if (score >= 80) return "bg-emerald-100 text-emerald-700";
  if (score >= 60) return "bg-yellow-100 text-yellow-700";
  return "bg-slate-100 text-slate-600";
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

interface Props {
  opportunity: Opportunity;
  onMarkPosted: (oppId: string, postedUrl?: string) => void;
  onSkip: (oppId: string, reason?: string) => void;
  onEditDraft: (oppId: string, newDraft: string) => void;
  onRedraft: (oppId: string) => void;
  isRedrafting?: boolean;
}

export function OpportunityCard({ opportunity: opp, onMarkPosted, onSkip, onEditDraft, onRedraft, isRedrafting }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(opp.draft_response || "");
  const [copied, setCopied] = useState(false);

  const engagement = opp.thread_engagement as Record<string, number> | null;
  const engagementText = engagement
    ? [
        engagement.upvotes && `${engagement.upvotes} pts`,
        engagement.comments && `${engagement.comments} comments`,
        engagement.likes && `${engagement.likes} likes`,
      ].filter(Boolean).join(" / ")
    : "";

  const handleCopy = async () => {
    if (opp.draft_response) {
      await navigator.clipboard.writeText(opp.draft_response);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveDraft = () => {
    onEditDraft(opp.id, draftText);
    setEditing(false);
  };

  return (
    <div className={`rounded-lg border transition-colors ${
      opp.status === "posted" ? "border-emerald-200 bg-emerald-50/30" :
      opp.status === "skipped" ? "border-slate-200 bg-slate-50/50 opacity-60" :
      "border-slate-200 bg-white hover:border-slate-300"
    }`}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PLATFORM_COLORS[opp.platform]}`}>
              {PLATFORM_LABELS[opp.platform]}
            </span>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreColor(opp.score_total)}`}>
              {opp.score_total}
            </span>
            {opp.thread_posted_at && (
              <span className="text-xs text-slate-400">{timeAgo(opp.thread_posted_at)}</span>
            )}
            {engagementText && (
              <span className="text-xs text-slate-400">{engagementText}</span>
            )}
            {opp.status === "posted" && (
              <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Posted</span>
            )}
            {opp.status === "skipped" && (
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Skipped</span>
            )}
          </div>
          <h3 className="text-sm font-medium text-slate-900 line-clamp-2">
            {opp.thread_title || opp.thread_url}
          </h3>
          {opp.thread_subreddit && (
            <span className="mt-0.5 text-xs text-slate-400">r/{opp.thread_subreddit}</span>
          )}
        </div>
        <span className="mt-1 text-slate-300">{expanded ? "\u25B2" : "\u25BC"}</span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-100 p-4">
          {/* Score breakdown */}
          <div className="mb-4 grid grid-cols-5 gap-2 text-center">
            {([
              ["Relevance", opp.score_relevance],
              ["Recency", opp.score_recency],
              ["Engagement", opp.score_engagement],
              ["Openness", opp.score_openness],
              ["Safety", opp.score_safety],
            ] as [string, number][]).map(([label, score]) => (
              <div key={label} className="rounded-lg bg-slate-50 p-2">
                <div className="text-xs text-slate-400">{label}</div>
                <div className="text-sm font-semibold text-slate-700">{score}</div>
              </div>
            ))}
          </div>

          {/* Draft response */}
          {opp.draft_response && !editing && (
            <div className="mb-4">
              <div className="mb-1 text-xs font-medium text-slate-500">Draft Response</div>
              <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                {opp.draft_response}
              </div>
            </div>
          )}

          {/* Draft editor */}
          {editing && (
            <div className="mb-4">
              <div className="mb-1 text-xs font-medium text-slate-500">Edit Draft</div>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                rows={8}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleSaveDraft}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(false); setDraftText(opp.draft_response || ""); }}
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {opp.status === "pending" && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setEditing(true)}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
              >
                Edit
              </button>
              <button
                onClick={handleCopy}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
              <button
                onClick={() => onRedraft(opp.id)}
                disabled={isRedrafting}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-50"
              >
                {isRedrafting ? "Redrafting..." : "Redraft"}
              </button>
              <a
                href={opp.thread_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
              >
                Open Thread
              </a>
              <button
                onClick={() => onMarkPosted(opp.id)}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                Mark Posted
              </button>
              <button
                onClick={() => onSkip(opp.id)}
                className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                Skip
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
